import { useEffect, useRef } from "react";

const SITE_URL = "https://kakobuyspreadsheet.watch";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = "/",
  ogType = "website",
  jsonLd,
}: SEOHeadProps) {
  const jsonLdRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);

    const canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    const canonicalHref = canonicalPath === "/" ? SITE_URL : `${SITE_URL}${canonicalPath}`;
    if (canonical) {
      canonical.setAttribute("href", canonicalHref);
    } else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", canonicalHref);
      document.head.appendChild(link);
    }

    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", canonicalHref, true);
    setMeta("og:type", ogType, true);
    setMeta("og:locale", "pl_PL", true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // Remove old JSON-LD
    if (jsonLdRef.current && jsonLdRef.current.parentNode) {
      jsonLdRef.current.parentNode.removeChild(jsonLdRef.current);
      jsonLdRef.current = null;
    }

    // Inject new JSON-LD
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      const data = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      script.textContent = JSON.stringify(
        data.length === 1 ? data[0] : { "@graph": data },
        null,
        2
      );
      document.head.appendChild(script);
      jsonLdRef.current = script;
    }

    return () => {
      if (jsonLdRef.current && jsonLdRef.current.parentNode) {
        jsonLdRef.current.parentNode.removeChild(jsonLdRef.current);
        jsonLdRef.current = null;
      }
    };
  }, [title, description, keywords, canonicalPath, ogType, jsonLd]);

  return null;
}